import React, { useEffect, useMemo, useReducer } from "react";
import { useUser, useToggleSaveJobPost, useToggleFollowCompany } from "@/hooks";
import { SavedCompany, User, SavedJobPost } from "@/types/user";
import { JobPost } from "@/types/jobPost";
import { Company } from "@/types/company";
import { UserContext } from "./contexts";

export interface State {
  dispatch: React.Dispatch<UserAction>;
  user: User | null;
  refetchUser: () => Promise<User | null>;

  userId: User["id"] | null;
  authToken: string | null;
  isAuthenticated: boolean;
  disabledBasicContent: boolean;
  disabledProContent: boolean;
  isSuperUser: boolean;

  loading: boolean;
  error: string | null;

  savedJobs: SavedJobPost[];
  savedCompanies: SavedCompany[];
  toggleSaveJobPost: (jobPostId: JobPost["id"]) => void;
  toggleFollowCompany: (companyId: Company["id"]) => void;
  jobPostError: string | null;
  companyError: string | null;
  isCompanyFollowed: (companyId: Company["id"]) => boolean;
  isJobSaved: (jobPostId: JobPost["id"]) => boolean;
}

const initialState: State = {
  dispatch: () => {},
  user: null,
  refetchUser: async () => null,
  userId: null,
  authToken: null,
  disabledBasicContent: true,
  disabledProContent: true,
  isAuthenticated: false,
  isSuperUser: false,

  loading: false,
  error: null,

  savedJobs: [],
  savedCompanies: [],
  toggleSaveJobPost: () => {},
  toggleFollowCompany: () => {},
  jobPostError: null,
  companyError: null,

  isCompanyFollowed: () => false,
  isJobSaved: () => false,
};

export type UserAction =
  | { type: "SET_USER"; payload: User | null }
  | { type: "SET_USER_ID"; payload: User["id"] | null }
  | { type: "SET_AUTHTOKEN"; payload: string | null }
  | { type: "SET_BASIC_DISABLED_CONTENT"; payload: boolean }
  | { type: "SET_PRO_DISABLED_CONTENT"; payload: boolean }
  | { type: "SET_SUPER_USER"; payload: boolean }
  | { type: "SET_ERROR"; payload: string | null }
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "SET_SAVED_JOBS"; payload: SavedJobPost[] }
  | { type: "SET_SAVED_COMPANIES"; payload: SavedCompany[] }
  | { type: "SET_JOB_POST_ERROR"; payload: string | null }
  | { type: "SET_COMPANY_ERROR"; payload: string | null }
  | { type: "RESET_STATE" };

function userReducer(state: State, action: UserAction): State {
  switch (action.type) {
    case "SET_USER":
      return { ...state, user: action.payload };
    case "SET_USER_ID":
      return { ...state, userId: action.payload };
    case "SET_AUTHTOKEN":
      return { ...state, authToken: action.payload };
    case "SET_BASIC_DISABLED_CONTENT":
      return { ...state, disabledBasicContent: action.payload };
    case "SET_PRO_DISABLED_CONTENT":
      return { ...state, disabledProContent: action.payload };
    case "SET_SUPER_USER":
      return { ...state, isSuperUser: action.payload };
    case "SET_ERROR":
      return { ...state, error: action.payload };
    case "SET_LOADING":
      return { ...state, loading: action.payload };

    case "SET_SAVED_JOBS":
      return { ...state, savedJobs: action.payload };
    case "SET_SAVED_COMPANIES":
      return { ...state, savedCompanies: action.payload };
    case "SET_JOB_POST_ERROR":
      return { ...state, jobPostError: action.payload };
    case "SET_COMPANY_ERROR":
      return { ...state, companyError: action.payload };

    case "RESET_STATE":
      return initialState;
    default:
      return state;
  }
}

interface UserProviderProps {
  children: React.ReactNode;
}

export function UserProvider({ children }: UserProviderProps) {
  const [state, dispatch] = useReducer(userReducer, {
    ...initialState,
    userId: localStorage.getItem("userId")
      ? parseInt(localStorage.getItem("userId")!)
      : null,
    authToken: localStorage.getItem("authToken") ?? null,
  });

  const { data, loading, error, refetch } = useUser(
    state.userId ?? 0,
    state.authToken,
  );

  const {
    data: toggleJobData,
    toggleSaveJobPost,
    loading: toggleJobLoading,
    error: toggleJobError,
  } = useToggleSaveJobPost();

  const {
    data: toggleCompanyData,
    toggleFollowCompany,
    loading: toggleCompanyLoading,
    error: toggleCompanyError,
  } = useToggleFollowCompany();

  const isAuthenticated = !!state.user;

  const isLoading =
    state.loading || loading || toggleJobLoading || toggleCompanyLoading;

  const combinedError =
    state.error || error || toggleJobError || toggleCompanyError;

  useEffect(() => {
    if (!state.userId || state.userId <= 0 || !state.authToken) return;

    const loadData = async () => {
      dispatch({ type: "SET_LOADING", payload: true });
      dispatch({ type: "SET_ERROR", payload: null });

      try {
        const fetchedUser = await refetch();
        dispatch({ type: "SET_USER", payload: fetchedUser ?? null });

        const superUser = fetchedUser?.is_superuser ?? false;
        dispatch({ type: "SET_SUPER_USER", payload: superUser });

        const savedJobs = data?.saved_job_posts || [];
        dispatch({
          type: "SET_SAVED_JOBS",
          payload: savedJobs,
        });

        const savedCompanies = data?.saved_companies || [];

        dispatch({
          type: "SET_SAVED_COMPANIES",
          payload: savedCompanies,
        });
      } catch (err) {
        console.error("🛑 Failed to fetch user", err);

        if (err instanceof Error) {
          dispatch({ type: "SET_ERROR", payload: err.message });
        } else {
          dispatch({
            type: "SET_ERROR",
            payload: "An unexpected error occurred.",
          });
        }
      } finally {
        dispatch({ type: "SET_LOADING", payload: false });
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.userId, state.authToken]);

  useEffect(() => {
    const type = data?.membership_type?.name;

    if (type === "basic") {
      dispatch({ type: "SET_BASIC_DISABLED_CONTENT", payload: true });
      dispatch({ type: "SET_PRO_DISABLED_CONTENT", payload: true });
    } else if (type === "pro") {
      dispatch({ type: "SET_BASIC_DISABLED_CONTENT", payload: false });
      dispatch({ type: "SET_PRO_DISABLED_CONTENT", payload: true });
    } else if (type === "premium") {
      dispatch({ type: "SET_BASIC_DISABLED_CONTENT", payload: false });
      dispatch({ type: "SET_PRO_DISABLED_CONTENT", payload: false });
    }
  }, [data?.membership_type?.name]);

  useEffect(() => {
    const savedJobs = (data && data.saved_job_posts) || [];
    const savedCompanies = (data && data.saved_companies) || [];

    if (savedJobs.length > 0) {
      dispatch({ type: "SET_SAVED_JOBS", payload: savedJobs });
    }

    if (savedCompanies.length > 0) {
      dispatch({ type: "SET_SAVED_COMPANIES", payload: savedCompanies });
    }
  }, [data?.saved_job_posts, data?.saved_companies]);

  const value = useMemo(() => {
    const handleToggleSaveJobPost = async (jobPostId: JobPost["id"]) => {
      dispatch({ type: "SET_JOB_POST_ERROR", payload: null });

      try {
        const response = await toggleSaveJobPost({ job_post_id: jobPostId });

        const updatedJob = response?.saved_job;
        if (!updatedJob) return;

        if (typeof updatedJob.saved_status === "boolean") {
          const exists = state.savedJobs.filter(
            job => job.job_post_id !== updatedJob.job_post_id,
          );

          if (updatedJob.saved_status) {
            dispatch({
              type: "SET_SAVED_JOBS",
              payload: [...exists, updatedJob],
            });
          }
          if (!updatedJob.saved_status) {
            dispatch({
              type: "SET_SAVED_JOBS",
              payload: exists,
            });
          }
        }
      } catch (err) {
        if (err instanceof Error) {
          dispatch({ type: "SET_JOB_POST_ERROR", payload: err.message });
        } else {
          dispatch({
            type: "SET_JOB_POST_ERROR",
            payload: "An unknown error occurred.",
          });
        }
      }
    };

    const handleToggleFollowCompany = async (companyId: Company["id"]) => {
      dispatch({ type: "SET_COMPANY_ERROR", payload: null });

      try {
        const response = await toggleFollowCompany({ company_id: companyId });

        const updatedCompany = response?.saved_company;
        if (!updatedCompany) return;

        if (
          updatedCompany.company_follow_status === "following" ||
          updatedCompany.company_follow_status === "unfollowed"
        ) {
          const exists = state.savedCompanies.some(
            company => company.id === updatedCompany.id,
          );

          const newList = exists
            ? state.savedCompanies.map(company =>
                company.id === updatedCompany.id ? updatedCompany : company,
              )
            : [...state.savedCompanies, updatedCompany];

          dispatch({ type: "SET_SAVED_COMPANIES", payload: newList });
        } else if (updatedCompany.company_follow_status === "not_following") {
          dispatch({
            type: "SET_SAVED_COMPANIES",
            payload: state.savedCompanies.filter(
              company => company.id !== updatedCompany.id,
            ),
          });
        }
      } catch (err) {
        if (err instanceof Error) {
          dispatch({ type: "SET_COMPANY_ERROR", payload: err.message });
        } else {
          dispatch({
            type: "SET_COMPANY_ERROR",
            payload: "An unknown error occurred.",
          });
        }
      }
    };

    const isCompanyFollowed = (companyId: Company["id"]) => {
      return state.savedCompanies.some(
        saved =>
          saved.company_id === companyId &&
          saved.company_follow_status === "following",
      );
    };

    const isJobSaved = (jobPostId: JobPost["id"]) => {
      return state.savedJobs.some(
        job => job.job_post_id === jobPostId && job.saved_status === true,
      );
    };

    const refetchUser = async (): Promise<User | null> => {
      if (!state.userId || !state.authToken) return null;

      dispatch({ type: "SET_LOADING", payload: true });
      dispatch({ type: "SET_ERROR", payload: null });

      try {
        const fetchedUser = await refetch();
        dispatch({ type: "SET_USER", payload: fetchedUser ?? null });

        const savedJobs = fetchedUser?.saved_job_posts || [];
        const savedCompanies = fetchedUser?.saved_companies || [];

        dispatch({ type: "SET_SAVED_JOBS", payload: savedJobs });
        dispatch({ type: "SET_SAVED_COMPANIES", payload: savedCompanies });

        return fetchedUser ?? null;
      } catch (err) {
        console.error("🛑 Failed to refetch user", err);
        dispatch({
          type: "SET_ERROR",
          payload:
            err instanceof Error
              ? err.message
              : "An unexpected error occurred.",
        });

        return null;
      } finally {
        dispatch({ type: "SET_LOADING", payload: false });
      }
    };
    return {
      dispatch,
      user: state.user,
      userId: state.userId,
      loading: isLoading,
      error: combinedError,
      authToken: state.authToken,
      refetchUser,
      isAuthenticated,
      disabledBasicContent: state.disabledBasicContent,
      disabledProContent: state.disabledProContent,
      isSuperUser: state.isSuperUser,

      savedJobs: state.savedJobs,
      savedCompanies: state.savedCompanies,
      toggleSaveJobPost: handleToggleSaveJobPost,
      toggleFollowCompany: handleToggleFollowCompany,
      jobPostError: state.jobPostError,
      companyError: state.companyError,
      isCompanyFollowed,
      isJobSaved,
    };
  }, [
    state,
    toggleSaveJobPost,
    toggleJobData,
    toggleFollowCompany,
    toggleCompanyData,
    isAuthenticated,
    isLoading,
    combinedError,
  ]);

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}
